import { Box, PopperProps } from '@material-ui/core';
import {
  SEmptyState,
  SHighlightText,
  SInfiniteScroll,
  SSelectDropdown,
  SelectDetailsButton,
  SelectMenuHeader
} from '@setvi/shared/components';
import { CSSProperties, useState, memo, useCallback, useMemo } from 'react';
import CheckRoundedIcon from '@material-ui/icons/CheckRounded';
import { TextColor } from '@setvi/shared/enums';
import { SSelectDetailsButtonProps } from '@setvi/shared/components/sforms/sselect/components/button';
import { STextProps } from '@setvi/shared/components/sui/stext';
import { StyledListItem } from './styled';
import { SkeletonLoader } from './skeleton-loader';

// Component constants
const DEFAULT_DIMENSIONS = {
  inputWidth: 250,
  dropdownWidth: 250,
  maxHeight: 350
} as const;

const DEFAULT_CONFIG = {
  placeholder: 'Select option',
  searchPlaceholder: 'Search options...',
  emptyMessage: 'No options found',
  searchable: true,
  clearable: true,
  closeOnSelect: true
} as const;

const COLORS = {
  selected: '#2196F3',
  disabled: '#ccc',
  subtext: TextColor.Grey
} as const;

const SPACING = {
  listItemMargin: '0px 10px'
} as const;

/**
 * Represents a selectable option in the dropdown
 *
 * @example
 * ```tsx
 * const option: Option = {
 *   value: 'user-123',
 *   label: 'John Doe',
 *   subtext: 'Senior Developer',
 *   icon: UserIcon,
 *   disabled: false,
 *   data: { email: 'john@example.com', role: 'admin' }
 * }
 * ```
 */
export interface Option {
  /** Optional icon component to display next to the label */
  icon?: React.ComponentType;
  /** Display text for the option */
  label: string;
  /** Optional secondary text displayed below the main label */
  subtext?: string;
  /** Unique identifier for the option */
  value: string;
  /** Whether this option is disabled */
  disabled?: boolean;
  /** Additional data that can be attached to the option */
  data?: any;
  /** This JSX element will be rendered instead of label */
  customItem?: JSX.Element;
}

/**
 * Styling configuration for the dropdown
 */
export interface DropdownStyles {
  /** Custom styles for the input trigger */
  input?: CSSProperties;
  /** Custom styles for the dropdown container */
  dropdown?: CSSProperties;
  /** Custom styles for individual options */
  option?: CSSProperties;
}

/**
 * Configuration for dropdown dimensions
 */
export interface DropdownDimensions {
  /** Width of the input trigger in pixels */
  inputWidth?: number;
  /** Width of the dropdown content in pixels */
  dropdownWidth?: number;
  /** Maximum height of the dropdown content in pixels */
  maxHeight?: number;
}

/**
 * Props for the BaseDropdown component
 */
export interface BaseDropdownProps {
  /** Array of available options */
  options: Option[];

  /** Title displayed in the dropdown header */
  title: string;

  /** Placeholder text when no option is selected */
  placeholder?: string;

  /** Placeholder text for the search input */
  searchPlaceholder?: string;

  /** Currently selected option */
  selected?: Option | null;

  /** Custom styling configuration */
  styles?: DropdownStyles;

  /** Dimension configuration */
  dimensions?: DropdownDimensions;

  /** Whether the dropdown is disabled */
  disabled?: boolean;

  /** Whether options are currently loading */
  isLoading?: boolean;

  /** Whether to show search functionality (header still shows if title or clearable) */
  searchable?: boolean;

  /** Whether to show clear button when an option is selected (also shows header) */
  clearable?: boolean;

  /** Dropdown placement relative to the trigger */
  placement?: PopperProps['placement'];

  /** Current search query value (controlled) */
  searchValue?: string;

  /** New design prop */
  anchorProps?: Pick<SSelectDetailsButtonProps, 'newDesign' | 'noBorder'>;

  /** Custom empty state message */
  emptyMessage?: string;

  /** Whether to close dropdown on option select */
  closeOnSelect?: boolean;

  /** Managing dropdown position and functionality */
  disablePortal?: boolean;

  /** ARIA label for accessibility */
  'aria-label'?: string;

  /** Test ID for testing purposes */
  'data-testid'?: string;

  /** Callback fired when an option is selected or cleared */
  onSelect: (option: Option | null) => void;

  /** Function to determine if an option is currently selected */
  isSelected?: (option: Option) => boolean;

  /** Callback for search input changes */
  onSearchChange?: (value: string) => void;

  /** Callback to fetch more options (for infinite scroll) */
  onFetchMore?: () => void;

  /** Style for Button SText  * */
  selectButtonProps?: {
    fontColor: string;
    fontWeight: STextProps['weight'];
    fontSize: string;
  };
}

/**
 * Individual option list item with highlighting and selection state
 */
const ListItem = memo(
  ({
    option,
    isSelected,
    handleSelect,
    searchValue
  }: {
    option: Option;
    isSelected: (option: Option) => boolean;
    handleSelect: (option: Option) => void;
    searchValue: string;
  }) => {
    const selected = isSelected(option);
    const handleClick = useCallback(
      () => handleSelect(option),
      [handleSelect, option]
    );
    const mainTextStyle = useMemo(() => {
      let color;
      if (selected) {
        color = COLORS.selected;
      } else if (option.disabled) {
        color = COLORS.disabled;
      } else {
        color = undefined;
      }

      return {
        margin: SPACING.listItemMargin,
        color,
        fontWeight: option.subtext ? '600' : 'normal'
      };
    }, [selected, option.disabled, option.subtext]);

    const subtextStyle = useMemo(() => {
      let color;
      if (selected) {
        color = COLORS.selected;
      } else if (option.disabled) {
        color = COLORS.disabled;
      } else {
        color = COLORS.subtext;
      }

      return {
        margin: SPACING.listItemMargin,
        marginTop: '2px',
        color,
        fontSize: '0.875rem'
      };
    }, [selected, option.disabled]);

    return (
      <StyledListItem
        onClick={option.disabled ? undefined : handleClick}
        button
        className={`${selected ? 'selected' : ''} ${option.disabled ? 'disabled' : ''}`}
        aria-selected={selected}
        aria-disabled={option.disabled}
        role="option"
        disabled={option.disabled}>
        <Box
          style={{
            display: 'flex',
            alignItems: option.subtext ? 'flex-start' : 'center',
            width: '100%'
          }}>
          {option.icon && (
            <Box
              style={{
                marginTop: option.subtext ? '2px' : '0',
                marginRight: '8px'
              }}>
              <option.icon />
            </Box>
          )}
          <Box
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column'
            }}>
            {option?.customItem || (
              <>
                <SHighlightText
                  value={option.label}
                  searchQuery={searchValue}
                  style={mainTextStyle}
                  maxLines={2}
                />
                {option.subtext && (
                  <SHighlightText
                    value={option.subtext}
                    searchQuery={searchValue}
                    style={subtextStyle}
                    maxLines={2}
                  />
                )}
              </>
            )}
          </Box>
        </Box>
        <Box
          style={{ display: 'flex', alignItems: 'center', marginLeft: '8px' }}>
          <CheckRoundedIcon
            fontSize="small"
            htmlColor={
              selected && !option.disabled ? COLORS.selected : 'transparent'
            }
            aria-hidden="true"
          />
        </Box>
      </StyledListItem>
    );
  }
);

/**
 * Container component for the list of options with infinite scroll support
 */
const OptionList = memo(
  ({
    searchValue,
    options,
    isLoading,
    onFetchMore,
    isSelected,
    handleSelect,
    emptyMessage
  }: Pick<
    BaseDropdownProps,
    'options' | 'isLoading' | 'onFetchMore' | 'isSelected' | 'searchValue'
  > & {
    handleSelect: (option: Option) => void;
    emptyMessage?: string;
  }) => {
    const renderOption = useCallback(
      (option: Option) => (
        <ListItem
          key={option.value}
          option={option}
          searchValue={searchValue || ''}
          isSelected={isSelected || (() => false)}
          handleSelect={handleSelect}
        />
      ),
      [searchValue, isSelected, handleSelect]
    );

    if (!options.length && !isLoading)
      return (
        <Box
          height={200}
          display="flex"
          alignItems="center"
          justifyContent="center">
          <SEmptyState subTitle={emptyMessage || DEFAULT_CONFIG.emptyMessage} />
        </Box>
      );

    return (
      <Box role="listbox" aria-label="Options list">
        <SInfiniteScroll
          list={options}
          keyGetter={option => option.value}
          fetchMore={onFetchMore}
          render={renderOption}
        />
        {isLoading && <SkeletonLoader />}
      </Box>
    );
  }
);

/**
 * A reusable dropdown component with search, infinite scroll, and selection capabilities.
 *
 * Features:
 * - Optional search functionality with highlighted results
 * - Header with title and clear button (shown when needed)
 * - Infinite scroll for large option lists
 * - Keyboard and screen reader accessibility
 * - Customizable styling and placement
 * - Loading states with skeleton placeholders
 * - Disabled state support
 * - Memoized for optimal performance
 *
 * @example
 * ```tsx
 * <BaseDropdown
 *   options={[
 *     { value: '1', label: 'Option 1' },
 *     { value: '2', label: 'Option 2', subtext: 'Additional info', disabled: true },
 *     { value: '3', label: 'Option 3', icon: MyIcon }
 *   ]}
 *   onSelect={(option) => console.log('Selected:', option)}
 *   title="My Dropdown"
 *   selected={selectedOption}
 *   isSelected={(option) => option.value === selectedValue}
 *   dimensions={{ inputWidth: 300, dropdownWidth: 350 }}
 *   styles={{ input: { borderRadius: '8px' } }}
 *   searchable
 *   clearable
 *   onSearchChange={(value) => setSearchQuery(value)}
 *   onFetchMore={() => loadMoreOptions()}
 * />
 * ```
 *
 */
export const BaseDropdown = memo(
  ({
    options,
    title,
    placeholder = DEFAULT_CONFIG.placeholder,
    searchPlaceholder = DEFAULT_CONFIG.searchPlaceholder,
    selected,
    styles,
    dimensions,
    disabled = false,
    disablePortal = false,
    placement,
    isLoading,
    searchable = DEFAULT_CONFIG.searchable,
    clearable = DEFAULT_CONFIG.clearable,
    closeOnSelect = DEFAULT_CONFIG.closeOnSelect,
    searchValue,
    anchorProps,
    emptyMessage = DEFAULT_CONFIG.emptyMessage,
    'aria-label': ariaLabel,
    'data-testid': testId,
    isSelected,
    onSelect,
    onSearchChange,
    onFetchMore,

    selectButtonProps
  }: BaseDropdownProps) => {
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);

    const handleSelect = useCallback(
      (option: Option) => {
        if (option.disabled) return;
        onSelect(option);
        if (closeOnSelect) {
          setAnchor(null);
        }
      },
      [onSelect, closeOnSelect]
    );

    const handleClear = useCallback(() => {
      onSelect(null);
      if (closeOnSelect) {
        setAnchor(null);
      }
    }, [onSelect, closeOnSelect]);

    const handleAnchorClick = useCallback(
      (e: React.MouseEvent<HTMLElement>) => {
        if (!disabled) {
          setAnchor(e.currentTarget);
        }
      },
      [disabled]
    );

    const handleClose = useCallback(() => {
      setAnchor(null);
    }, []);

    const isOpen = Boolean(anchor) && !disabled;

    // Derived values from props
    const inputWidth = dimensions?.inputWidth ?? DEFAULT_DIMENSIONS.inputWidth;
    const dropdownWidth =
      dimensions?.dropdownWidth ?? DEFAULT_DIMENSIONS.dropdownWidth;
    const maxHeight = dimensions?.maxHeight ?? DEFAULT_DIMENSIONS.maxHeight;

    return (
      <SSelectDropdown
        newDesign={anchorProps?.newDesign}
        disablePortal={disablePortal}
        placement={placement}
        anchor={
          <SelectDetailsButton
            width={inputWidth}
            style={styles?.input}
            title={selected?.label || placeholder}
            titleHighligted={!!selected}
            onClick={handleAnchorClick}
            open={isOpen}
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-label={ariaLabel || `${title} dropdown`}
            data-testid={testId}
            {...anchorProps}
            {...selectButtonProps}
          />
        }
        anchorElement={anchor}
        onClose={handleClose}
        content={
          <Box width={dropdownWidth} style={styles?.dropdown}>
            <Box maxHeight={maxHeight} overflow="hidden auto">
              {(searchable || (clearable && !!selected) || title) && (
                <SelectMenuHeader
                  title={title}
                  search={searchable}
                  placeholder={searchPlaceholder}
                  clearButton={clearable && !!selected}
                  handleClear={handleClear}
                  searchValue={searchValue}
                  handleChange={onSearchChange}
                />
              )}
              <Box mt={1}>
                <OptionList
                  searchValue={searchValue || ''}
                  options={options}
                  isLoading={isLoading}
                  onFetchMore={onFetchMore}
                  isSelected={isSelected || (() => false)}
                  handleSelect={handleSelect}
                  emptyMessage={emptyMessage}
                />
              </Box>
            </Box>
          </Box>
        }
      />
    );
  }
);
